export function reminderToday(now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "America/Ciudad_Juarez", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now).map(p => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}
// Shared by the PWA and scheduler. Derived reminders never move money.
export function validIsoDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function reminderDate(period, paymentDays) {
  const end = String(period).slice(-10);
  if (!validIsoDate(end) || !Number.isInteger(paymentDays) || paymentDays < 0 || paymentDays > 90) return null;
  const date = new Date(`${end}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + paymentDays);
  return date.toISOString().slice(0, 10);
}

// Owner-confirmed monthly deadlines; reminder start is fixed, never rolling.
const monthlyPaymentRules = {
  BBVA: { bankDay: 22, fromCutEnd: "2026-10-02" },
  Scotiabank: { bankDay: 5, fromCutEnd: "2026-09-16" },
};
export function cardPaymentSchedule(card, period, saved) {
  const rule = monthlyPaymentRules[card];
  const end = String(period).slice(-10);
  if (!validIsoDate(end)) return { dueOn: null, targetOn: null, confirmed: false, eligible: false };
  let dueOn = reminderDate(period, card === "AMEX Servicios" ? 13 : 20);
  if (rule) {
    const date = new Date(`${end}T12:00:00Z`);
    if (rule.bankDay <= date.getUTCDate()) date.setUTCMonth(date.getUTCMonth()+1,1);
    date.setUTCDate(rule.bankDay); dueOn = date.toISOString().slice(0,10);
  }
  const policyDate = new Date(`${dueOn}T12:00:00Z`);policyDate.setUTCDate(policyDate.getUTCDate()-2);
  const policyTarget = policyDate.toISOString().slice(0,10);
  const bankDate = saved && validIsoDate(saved.dueOn) ? new Date(`${saved.dueOn}T12:00:00Z`) : null;
  if (bankDate) { dueOn = saved.dueOn; bankDate.setUTCDate(bankDate.getUTCDate()-2); }
  const earlier = bankDate?.toISOString().slice(0,10);
  return { dueOn, targetOn: earlier && earlier < policyTarget ? earlier : policyTarget,
    confirmed: Boolean(rule || bankDate), eligible: !rule || end >= rule.fromCutEnd };
}

export function reminderStage(dueOn, today, confirmed) {
  if (!validIsoDate(dueOn) || !validIsoDate(today)) return null;
  const days = Math.round((Date.parse(`${dueOn}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86400000);
  if (days > 7) return null;
  return { days, severity: confirmed && days <= 0 ? "urgent" : "warning",
    label: !confirmed ? days < 0 ? "Fecha estimada pasada · confirma la fecha límite" : `Pago estimado${days === 0 ? " hoy" : ` en ${days} día${days === 1 ? "" : "s"}`} · confirma la fecha`
      : days < 0 ? "Fecha vencida · pago sin registrar" : days === 0 ? "Pago hoy · sin registrar" : `Pago en ${days} día${days === 1 ? "" : "s"}` };
}

/** Accepts normalized card config, original movements, payment events and deadlines. */
export function buildCardPaymentAlerts(snapshot, movements, events, cards, deadlines, today) {
  const paid = new Set(events.map(e => `${e.card ?? e.Tarjeta}|${e.period ?? e.closedPeriod ?? e["Periodo cerrado"]}`));
  const cuts = new Map();
  const key = (card, period) => `${card}|${period}`;
  const ensure = (card, period) => {
    const id = key(card, period);
    if (!cuts.has(id)) cuts.set(id, { card, period, amountCents: 0 });
    return cuts.get(id);
  };
  const rows = [...new Map([...(snapshot.movements ?? []), ...movements].map(m => [m.id, m])).values()];
  for (const m of rows) {
    if (m.kind !== "expense" || !validIsoDate(String(m.cardPeriod).slice(-10))) continue;
    if (m.cardPaymentStatus === "paid" || m.historicalSettlement) {
      const same = rows.filter(r => r.kind === "expense" && r.paymentMethod === m.paymentMethod && r.cardPeriod === m.cardPeriod);
      if (same.every(r => r.cardPaymentStatus === "paid" || r.historicalSettlement)) paid.add(key(m.paymentMethod, m.cardPeriod));
      continue;
    }
    if (m.installmentPurchaseId && m.installmentNumber !== 1 && m.bankPostingState !== "confirmed") continue;
    const activityDate = m.bankPostingState === "confirmed" ? m.bankPostingPostedOn ?? (m.bankPostingConfirmedAt ? new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(m.bankPostingConfirmedAt)) : String(m.spentOn ?? m.createdAt).slice(0, 10)) : String(m.spentOn ?? m.createdAt).slice(0, 10);
    if (!validIsoDate(activityDate) || activityDate > today) continue;
    ensure(m.paymentMethod, m.cardPeriod).amountCents += m.billedAmountCents ?? m.amountCents;
  }
  for (const r of snapshot.cardPayments ?? []) if (r.unassignedCents > 0 && validIsoDate(String(r.unassignedPeriod).slice(-10))) ensure(r.name, r.unassignedPeriod).amountCents += r.unassignedCents;
  for (const c of snapshot.bankCredits ?? []) if (c.postedOn <= today) ensure(c.card, c.period).amountCents -= c.amountCents;
  for (const a of snapshot.cardPaymentTimingAdjustments ?? []) {
    ensure(a.card, a.paymentPeriod).amountCents -= a.amountCents;
    ensure(a.card, a.sourcePeriod).amountCents += a.amountCents;
  }
  return [...cuts.values()].filter(c => c.amountCents > 0 && c.period.slice(-10) < today && !paid.has(key(c.card, c.period)))
    .flatMap(c => {
      const card = cards.find(card => card.paymentMethod === c.card && card.active !== false);
      if (!card) return [];
      const saved = deadlines.find(d => d.card === c.card && d.period === c.period);
      const {dueOn,targetOn,confirmed,eligible} = cardPaymentSchedule(c.card,c.period,saved);
      if (!eligible) return [];
      const stage = targetOn ? reminderStage(targetOn, today, true) : null;
      const bankStage = confirmed && stage?.days <= 0 && dueOn >= today
        ? {label: dueOn === today ? "Último día del banco · pago sin registrar" : stage.days < 0 ? "Objetivo pasado · aún dentro del plazo bancario" : "Objetivo hoy · aún dentro del plazo bancario", severity: dueOn === today ? "urgent" : "warning"}
        : confirmed && dueOn < today ? {label:"Límite bancario pasado · pago sin registrar",severity:"urgent"} : null;
      return [{ ...c, dueOn, targetOn, confirmed, ...stage,
        label: stage?.label?.replace("Fecha vencida", "Fecha objetivo pasada").replace("Pago hoy", "Pago objetivo hoy") ?? "Próximo pago objetivo", severity: stage?.severity ?? "info", days: stage?.days ?? null, ...bankStage }];
    }).sort((a, b) => (a.targetOn ?? "9999").localeCompare(b.targetOn ?? "9999") || a.card.localeCompare(b.card));
}

export function cardReminderMessage(alert) {
  const url = `/?notification=card-payment&card=${encodeURIComponent(alert.card)}&period=${encodeURIComponent(alert.period)}`;
  return { data: { title: `Pago de ${alert.card}`, body: `${alert.label}. Fecha objetivo de Banculem. Toca para revisar tu tarjeta.`, action: "card-payment", url,
    tag: `card-payment-${alert.card}-${alert.period}` }, webpush: { headers: { TTL: "21600", Urgency: "normal" } } };
}

export function shouldNotifyCardPayment(alert) {
  // Persistent in-app notice, bounded push frequency; no daily overdue spam.
  return alert.days !== null && [7, 3, 1, 0].includes(alert.days);
}
